import { readFile, rm } from "node:fs/promises";
import { ensureSecureDir, writeSecureFile } from "./secure-file.js";
import { join } from "node:path";


const SYNC_LOCK_STALE_MS = 5 * 60 * 1000; // 5 minutes

function syncLockPath(dataDir: string, channelId: string): string {
  const safe = channelId.replace(/[^a-zA-Z0-9_-]/g, "_");
  return join(dataDir, `mls-a2a-sync-${safe}.lock`);
}

/**
 * Acquire a channel-level A2A sync lock. Returns true if acquired,
 * false if another instance holds the lock. Uses atomic exclusive-create
 * (wx flag) to prevent TOCTOU races.
 *
 * Stale locks (>5 min old) are automatically cleaned up. This handles
 * gateway crashes between lock acquisition and Welcome processing.
 * After a crash, the first sync attempt on restart may encounter a stale
 * lock — it will be deleted and the lock re-acquired on the same call.
 */
export async function acquireA2ASyncLock(
  dataDir: string,
  channelId: string,
): Promise<boolean> {
  await ensureSecureDir(dataDir);
  const lockFile = syncLockPath(dataDir, channelId);
  const content = JSON.stringify({ pid: process.pid, timestamp: Date.now() });
  try {
    await writeSecureFile(lockFile, content, "wx");
    return true;
  } catch (err: any) {
    if (err.code !== "EEXIST") throw err;
    // Lock exists — check staleness
    try {
      const raw = await readFile(lockFile, "utf-8");
      const parsed = JSON.parse(raw);
      if (typeof parsed.timestamp === "number" && Date.now() - parsed.timestamp > SYNC_LOCK_STALE_MS) {
        // Stale lock — clean up and retry once
        await rm(lockFile).catch(() => {});
        try {
          await writeSecureFile(lockFile, content, "wx");
          return true;
        } catch {
          return false; // Another instance beat us to it
        }
      }
    } catch {
      // Can't read lock file — treat as held
    }
    return false;
  }
}

/**
 * Release the channel-level A2A sync lock. Idempotent — no error if
 * the lock file does not exist.
 */
export async function releaseA2ASyncLock(
  dataDir: string,
  channelId: string,
): Promise<void> {
  try {
    await rm(syncLockPath(dataDir, channelId));
  } catch { /* ignore ENOENT */ }
}

function mlsFileName(groupId: string): string {
  // Sanitize groupId for filesystem safety
  const safe = groupId.replace(/[^a-zA-Z0-9_-]/g, "_");
  return `mls-${safe}.json`;
}

const pendingKpPath = (dataDir: string, groupId: string) =>
  join(dataDir, `mls-kp-pending-${groupId.replace(/[^a-zA-Z0-9_-]/g, "_")}.json`);

/**
 * Check if a pending Welcome KP bundle already exists for a group.
 */
export async function hasPendingWelcome(
  dataDir: string,
  groupId: string,
): Promise<boolean> {
  try {
    await readFile(pendingKpPath(dataDir, groupId));
    return true;
  } catch {
    return false;
  }
}

/**
 * Save a pending KeyPackage bundle to disk.
 * The KP bundle has complex typed fields — we serialize the private key material
 * (raw Uint8Arrays) to base64, and the public package as pre-serialized bytes.
 * `serializePublicFn` should be MLSGroupManager.serializeKeyPackage.
 */
export async function savePendingKpBundle(
  dataDir: string,
  groupId: string,
  kp: any,
  serializePublicFn: (pub: any) => Uint8Array,
): Promise<void> {
  await ensureSecureDir(dataDir);
  const serialized = {
    publicPackageBytes: Buffer.from(serializePublicFn(kp.publicPackage)).toString("base64"),
    privatePackage: {
      initPrivateKey: Buffer.from(kp.privatePackage.initPrivateKey).toString("base64"),
      hpkePrivateKey: Buffer.from(kp.privatePackage.hpkePrivateKey).toString("base64"),
      signaturePrivateKey: Buffer.from(kp.privatePackage.signaturePrivateKey).toString("base64"),
    },
  };
  await writeSecureFile(pendingKpPath(dataDir, groupId), JSON.stringify(serialized));
}

/**
 * Load a pending KeyPackage bundle from disk.
 * Returns the raw private key material as Uint8Arrays.
 * The public package is returned as raw serialized bytes — the caller
 * must deserialize it if needed, or the Welcome handler can use just the private keys.
 */
export async function loadPendingKpBundle(
  dataDir: string,
  groupId: string,
): Promise<any | null> {
  try {
    const raw = await readFile(pendingKpPath(dataDir, groupId), "utf-8");
    const parsed = JSON.parse(raw);
    return {
      publicPackageBytes: new Uint8Array(Buffer.from(parsed.publicPackageBytes, "base64")),
      privatePackage: {
        initPrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.initPrivateKey, "base64")),
        hpkePrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.hpkePrivateKey, "base64")),
        signaturePrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.signaturePrivateKey, "base64")),
      },
    };
  } catch {
    return null;
  }
}

/**
 * Clear the pending KP bundle (after successful Welcome join).
 */
export async function clearPendingWelcome(
  dataDir: string,
  groupId: string,
): Promise<void> {
  try {
    await rm(pendingKpPath(dataDir, groupId));
  } catch { /* ignore */ }
}

/**
 * Save MLS group state to a per-group file.
 * State is an opaque serialized string from MLSGroupManager.exportState().
 */
export async function saveMlsState(
  dataDir: string,
  groupId: string,
  state: string,
): Promise<void> {
  await ensureSecureDir(dataDir);
  const filePath = join(dataDir, mlsFileName(groupId));
  await writeSecureFile(filePath, state);
}

/**
 * Load MLS group state from a per-group file.
 * Returns null if the file doesn't exist (first join or after re-join cleanup).
 */
export async function loadMlsState(
  dataDir: string,
  groupId: string,
): Promise<string | null> {
  try {
    const filePath = join(dataDir, mlsFileName(groupId));
    return await readFile(filePath, "utf-8");
  } catch {
    return null;
  }
}

/**
 * Delete MLS group state file.
 * Used when re-joining a group (old state is invalid after re-join).
 */
export async function deleteMlsState(
  dataDir: string,
  groupId: string,
): Promise<void> {
  try {
    const filePath = join(dataDir, mlsFileName(groupId));
    await rm(filePath);
  } catch {
    // Already removed or never existed
  }
}
